<template>
  <div class="flex min-h-screen bg-gray-50 dark:bg-gray-900">

    <!-- Sidebar -->
    <AcdSidebar :isOpen="isSidebarOpen" />

    <!-- Main content -->
    <main class="flex-1 transition-all duration-300" :class="{'ml-64': isSidebarOpen, 'ml-0': !isSidebarOpen}">
      <!-- Topbar -->
      <header class="fixed top-0 w-full z-50 bg-white dark:bg-gray-800 shadow">
        <AcdTopbar
          :page-title="currentPageTitle"
          :is-sidebar-open="isSidebarOpen"
          :is-dark-mode="isDarkMode"
          @toggle-sidebar="toggleSidebar"
          @toggle-dark-mode="toggleDarkMode"
        />
      </header>

      <div class="p-6 mt-16">

        <!-- Search + Add Term -->
        <div class="flex justify-between mb-6 p-4">
          <input
            v-model="search"
            type="text"
            placeholder="Search term..."
            class="border rounded-lg px-4 py-2 w-72 dark:bg-gray-700 dark:text-white"
          />
          <button
            @click="openModal"
            class="bg-[#74bd54] text-white px-5 py-2 rounded-lg hover:bg-[#63a43f]"
          >
            + Add Term
          </button>
        </div>

        <!-- Terms Grid -->
        <div v-if="filteredTerms.length" class="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div v-for="term in filteredTerms" :key="term.id" class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow hover:shadow-lg">
            
            <div class="flex items-center justify-between mb-4">
              <div>
                <h3 class="font-semibold text-gray-800 dark:text-white">{{ term.name }}</h3>
                <p class="text-sm text-gray-500">{{ term.code }}</p>
              </div>
              <div class="flex space-x-2">
                <button @click="editTerm(term)" class="text-blue-600 hover:underline text-sm">Edit</button>
                <button @click="deleteTerm(term.id)" class="text-red-500 hover:underline text-sm">Delete</button>
              </div>
            </div>

            <p class="text-gray-500 text-sm mb-2">{{ term.description }}</p>
            <p class="text-xs text-gray-400">From {{ term.start_date }} to {{ term.end_date }}</p>
          </div>
        </div>

        <div v-else class="text-center text-gray-400 mt-20">No terms created yet</div>

        <!-- Modal -->
        <Transition name="modal">
          <div v-if="showModal" class="fixed inset-0 bg-black/40 flex items-center justify-center">
            <div class="bg-white dark:bg-gray-800 rounded-xl p-6 w-[420px]">
              <h3 class="text-lg font-semibold text-[#035a76] mb-6">
                {{ editing ? "Edit Term" : "Create Term" }}
              </h3>
              <form @submit.prevent="saveTerm" class="space-y-4">

                <input v-model="form.name" type="text" placeholder="Term Name" required class="w-full border rounded p-2 dark:bg-gray-700 dark:text-white" />
                <input v-model="form.code" type="text" placeholder="Code (e.g., FT2026)" class="w-full border rounded p-2 dark:bg-gray-700 dark:text-white" />
                 <select
                  v-model="form.year"
                  required
                  class="w-full border border-gray-300  focus:outline-none focus:ring focus:ring-secondary  dark:border-gray-600 rounded p-2 dark:bg-gray-700 dark:text-white"
                >
                  <option disabled value="">Select Academic Year</option>
                            
                  <option v-for="year in filteredYears" :value="year.id" :key="year.id">
                    {{ year.name }}
                  </option>
              
                </select>                
                <input v-model="form.start_date" type="date" required class="w-full border rounded p-2 dark:bg-gray-700 dark:text-white" />
                <input v-model="form.end_date" type="date" required class="w-full border rounded p-2 dark:bg-gray-700 dark:text-white" />
                <textarea v-model="form.description" placeholder="Description" class="w-full border rounded p-2 dark:bg-gray-700 dark:text-white"></textarea>

                <div class="flex justify-end space-x-3">
                  <button type="button" @click="closeModal" class="px-4 py-2 bg-gray-300 rounded">Cancel</button>
                  <button type="submit" class="px-4 py-2 bg-[#74bd54] text-white rounded hover:bg-[#63a43f]">Save</button>
                </div>
              </form>
            </div>
          </div>
        </Transition>

      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import api from "@/services/api"
import AcdSidebar from "@/components/acd/AcdSidebar.vue";
import AcdTopbar from "@/components/acd/AcdTopbar.vue";

const currentPageTitle = ref("Academic Terms");
const isSidebarOpen = ref(true);
const isDarkMode = ref(false);

const showModal = ref(false);
const editing = ref(false);
const search = ref("");

const academicYears = ref([])

const terms = ref([]);

// const terms = ref([
//   { id: 1, name: "First Term 2026", code: "FT2026", start_date: "2026-01-10", end_date: "2026-04-30", description: "First term of 2026 academic year" },
//   { id: 2, name: "Second Term 2026", code: "ST2026", start_date: "2026-05-01", end_date: "2026-08-31", description: "Second term of 2026 academic year" }
// ]);

const form = ref({ id: null, name: "", code: "", start_date: "", end_date: "", description: "", year: "" });

const filteredTerms = computed(() => {
  return terms.value.filter(t => t.name.toLowerCase().includes(search.value.toLowerCase()));
});


const fetchAcademicYears = async () => {
  try {
    const response = await api.get("AcademicYears.php");
    academicYears.value = response.data;
  } catch (error) {
    console.error(error);
  }
}

const filteredYears = computed(() => academicYears.value.filter(y => y.name.toLowerCase().includes(search.value.toLowerCase())));

const openModal = () => {
  editing.value = false;
  form.value = { id: null, name: "", code: "", start_date: "", end_date: "", description: "", year: ""};
  showModal.value = true;
};

const editTerm = (term) => {
  editing.value = true;
  form.value = { ...term };
  showModal.value = true;
};



const fetchTerms = async () => {
  try {
    const response = await api.get("Terms.php");
    terms.value = response.data.map(term => ({
      id: term.id,
      name: term.name,
      code: term.code,
      start_date: term.start_date,
      end_date: term.end_date,
      description: term.description,
      year: term.academic_year_name
    }))
  } catch (error) {
    console.error(error);
  }
}

// const saveTerm = () => {
//   if (editing.value) {
//     const response = api.put("Terms.php", form.value);
//     const index = terms.value.findIndex(t => t.id === form.value.id);
//     terms.value[index] = { ...form.value };
//   } else {
//     terms.value.push({ id: Date.now(), ...form.value });
//   }
//   closeModal();
// };


const saveTerm = async () => {
  try {
    let response;

    if (editing.value) {
      response = await api.put("Terms.php", form.value);

      const index = terms.value.findIndex(t => t.id === form.value.id);
      if (index !== -1) {
        terms.value[index] = { ...form.value };
      }

    } else {
      response = await api.post("Terms.php", form.value);

      terms.value.push({
        id: response.data.id,
        name: response.data.name,
        code: response.data.code,
        start_date: response.data.start_date,
        end_date: response.data.end_date,
        description: response.data.description,
        year: response.data.year
      });
    }

    closeModal();

  } catch (error) {
    console.error("Error saving term:", error);
  }
};
const deleteTerm = (id) => {
  terms.value = terms.value.filter(t => t.id !== id);
};

const closeModal = () => showModal.value = false;

const toggleSidebar = () => isSidebarOpen.value = !isSidebarOpen.value;
const toggleDarkMode = () => {
  isDarkMode.value = !isDarkMode.value;
  if (isDarkMode.value) document.documentElement.classList.add("dark");
  else document.documentElement.classList.remove("dark");
};

onMounted (() => {
    fetchAcademicYears();
    fetchTerms();
})
</script>

<style>
.modal-enter-active,
.modal-leave-active { transition: all .25s ease; }
.modal-enter-from { opacity: 0; transform: scale(.9); }
.modal-leave-to { opacity: 0; transform: scale(.9); }
</style>